import { isFixedIncome } from './otc.helper';

export interface AssetClassDef {
  key: string;
  label: string;
  /** ng-zorro tag colour */
  color: string;
  /**
   * Chart fill: validated categorical slots in fixed order, so a class keeps
   * its colour whichever classes a client actually holds.
   */
  chartColor: string;
}

/** Display order of the asset-class breakdown. */
export const ASSET_CLASSES: AssetClassDef[] = [
  { key: 'fiat', label: 'Fiat', color: 'orange', chartColor: '#2a78d6' },
  {
    key: 'equity_etf',
    label: 'Equity and ETFs',
    color: 'green',
    chartColor: '#eb6834',
  },
  { key: 'bonds', label: 'Bonds', color: 'geekblue', chartColor: '#1baf7a' },
  {
    key: 'mutual_fund',
    label: 'Mutual Fund',
    color: 'purple',
    chartColor: '#eda100',
  },
  { key: 'commodity', label: 'Commodity', color: 'cyan', chartColor: '#e87ba4' },
  {
    key: 'derivatives',
    label: 'Derivatives',
    color: 'magenta',
    chartColor: '#4a3aa7',
  },
  { key: 'crypto', label: 'Crypto', color: 'blue', chartColor: '#008300' },
];

/**
 * Equity-like instrument classes for admin UI gates (form sections, tags,
 * instrument info). Exact ids only for swap/forward so FX wording cannot
 * false-match; equity/stock keep the existing description-style fallbacks.
 */
export function isEquityLikeAssetClass(
  id: string | null | undefined
): boolean {
  const raw = String(id || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (raw === 'equity' || raw === 'swap' || raw === 'forward') return true;
  return raw.includes('equity') || raw.includes('stock');
}

/**
 * Asset class of a balance line. Securities come back from the balances API as
 * type 'stock'; their real class is the instrument's assetClassId (with
 * descriptionAssetClass as fallback, as the two are filled inconsistently).
 * Equities, ETFs and any unrecognised class share one section.
 * Options, swaps and forwards share the Derivatives section.
 */
export function getAssetClass(holding: {
  type?: string | null;
  assetClassId?: string | null;
  descriptionAssetClass?: string | null;
}): string {
  if (holding.type === 'fiat') return 'fiat';
  if (holding.type === 'crypto') return 'crypto';
  if (isFixedIncome(holding)) return 'bonds';
  const raw = String(holding.assetClassId || holding.descriptionAssetClass || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (raw === 'mutual_fund' || raw === 'mutual_funds') return 'mutual_fund';
  if (raw === 'commodity' || raw === 'commodities') return 'commodity';
  if (
    raw === 'option' ||
    raw === 'options' ||
    raw === 'swap' ||
    raw === 'forward'
  ) {
    return 'derivatives';
  }
  return 'equity_etf';
}

/**
 * Groups holdings by asset class in ASSET_CLASSES order, skipping empty
 * classes. Items are sorted by value, largest first; pct is 0–100.
 */
export function groupByAssetClass<T>(
  holdings: T[],
  classOf: (h: T) => string,
  valueOf: (h: T) => number,
  grandTotal: number
) {
  return ASSET_CLASSES.map((cls) => {
    const items = holdings
      .filter((h) => classOf(h) === cls.key)
      .sort((a, b) => valueOf(b) - valueOf(a));
    const total = items.reduce((sum, h) => sum + valueOf(h), 0);
    return {
      ...cls,
      items,
      total,
      pct: grandTotal > 0 ? (total / grandTotal) * 100 : 0,
    };
  }).filter((g) => g.items.length > 0);
}
